import React, { useState, useMemo } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { Button } from './ui/button';
import {
  Command, CommandEmpty, CommandInput, CommandItem, CommandList,
} from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '../lib/utils';

/**
 * Combobox dipendenti con ricerca in-place.
 * Sostituisce il <Select> quando la lista supera ~15 elementi.
 *
 * Props:
 *   value          — staff_id selezionato (o 'none' se allowNone)
 *   onChange(id)   — chiamato con nuovo staff_id
 *   staffList      — array di {staff_id, first_name, last_name, display_name?, role_in_store?, role?}
 *   allowNone      — se true mostra opzione "— Salta —" con value='none'
 *   noneLabel      — label opzione none (default "— Nessuno —")
 *   placeholder    — testo quando value è vuoto
 *   testId         — data-testid del trigger (opzionale)
 *   className      — classi extra sul trigger
 *   disabled       — disabilita il trigger
 */
export default function StaffCombobox({
  value, onChange, staffList = [],
  allowNone = false, noneLabel = '— Nessuno —',
  placeholder = 'Seleziona…', testId, className, disabled = false,
}) {
  const [open, setOpen] = useState(false);

  // Sort alfabetico stabile (italiano, case-insensitive). Se il chiamante
  // ha già ordinato, il sort qui è idempotente.
  const sorted = useMemo(() => (
    [...staffList].sort((a, b) => {
      const na = (a.display_name || `${a.first_name || ''} ${a.last_name || ''}`).trim().toLocaleLowerCase('it');
      const nb = (b.display_name || `${b.first_name || ''} ${b.last_name || ''}`).trim().toLocaleLowerCase('it');
      return na.localeCompare(nb, 'it');
    })
  ), [staffList]);

  const selected = sorted.find(s => s.staff_id === value);
  const selectedLabel = value === 'none'
    ? noneLabel
    : selected
      ? (selected.display_name || `${selected.first_name || ''} ${selected.last_name || ''}`.trim())
      : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'justify-between rounded-none font-normal text-left',
            !selectedLabel && 'text-slate-400',
            className,
          )}
          data-testid={testId}
        >
          <span className="truncate">{selectedLabel || placeholder}</span>
          <ChevronsUpDown className="w-4 h-4 shrink-0 opacity-50 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 rounded-none w-[var(--radix-popover-trigger-width)]"
        align="start"
      >
        <Command
          filter={(itemValue, search) => {
            // Case-insensitive substring, tollera accenti.
            const norm = (s) => (s || '').toLocaleLowerCase('it').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            return norm(itemValue).includes(norm(search)) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Cerca dipendente…" data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList>
            <CommandEmpty>Nessun risultato.</CommandEmpty>
            {allowNone && (
              <CommandItem
                value={noneLabel}
                onSelect={() => { onChange('none'); setOpen(false); }}
                data-testid={testId ? `${testId}-opt-none` : undefined}
              >
                <Check className={cn('mr-2 h-3 w-3', value === 'none' ? 'opacity-100' : 'opacity-0')} />
                <span className="italic text-slate-500">{noneLabel}</span>
              </CommandItem>
            )}
            {sorted.map(s => {
              const label = s.display_name || `${s.first_name || ''} ${s.last_name || ''}`.trim();
              const role = s.role_in_store || s.role;
              return (
                <CommandItem
                  key={s.staff_id}
                  value={label}
                  onSelect={() => { onChange(s.staff_id); setOpen(false); }}
                  data-testid={testId ? `${testId}-opt-${s.staff_id}` : undefined}
                >
                  <Check className={cn('mr-2 h-3 w-3', value === s.staff_id ? 'opacity-100' : 'opacity-0')} />
                  <span>{label}</span>
                  {role && <span className="ml-2 text-[10px] text-slate-500 uppercase tracking-wider">· {role}</span>}
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
